import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { Text } from '@figma/fpl-components';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { Skeleton } from '@/UI/Skeleton/Skeleton';
import { TextSmMd, TextSmRg, Typography } from '@/UI/AppTypography/AppTypography';
import { FlexCenVer } from '@/UI/styles';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { AsteriskIcon, FigmaAiCreditIcon } from '@/UI/Icons';
import { AppInfoIcon } from '@/UI/AppInfoIcon/AppInfoIcon';
import { useContentRect } from '@/hooks/useContentRect';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { roundToDecimalIfNotWhole } from '@/utils/numbers';
import { useWeavyToFigmaCredits } from '@/utils/credits';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_WEAVE_FILE_FIGMA_CREDITS_CONSUMPTION } from '@/consts/featureFlags';
import { ByokBadge } from '@/components/Common/ByokBadge';
import { getPromoTooltipTitle } from '@/components/Common/PriceInfo/promoTooltip';

const DEFAULT_WIDTH = 55;

function PriceCreditIcon({ legacy }: { legacy: 'figma' | 'weavy' }) {
  return legacy === 'figma' ? <FigmaAiCreditIcon width={16} height={16} /> : <AsteriskIcon width={16} height={16} />;
}

type PriceInfoCostProps = {
  cost?: number;
  isLoading: boolean;
  isDynamicCost: boolean;
  isPerSecond?: boolean;
  perSecondRate?: number;
  /** Show the "Free in beta" label beside the cost (the properties-panel "Total cost"). */
  showBetaLabel?: boolean;
  /** Undiscounted cost; when set and above `cost`, shown struck through with a promotion tooltip. */
  fullCost?: number;
  /** Promotion details for the tooltip (expiry). */
  promo?: { expiresAt?: string };
};

const FigmaPriceInfoCost = ({ cost, isLoading, isDynamicCost, showBetaLabel }: PriceInfoCostProps) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const infoRowHeight = isFplMigrationEnabled ? '24px' : '16px';
  const { value: chargesFigmaCredits } = useFeatureFlag(FF_WEAVE_FILE_FIGMA_CREDITS_CONSUMPTION);
  const weavyToFigmaCredits = useWeavyToFigmaCredits();
  const figmaCredits = cost === undefined ? undefined : weavyToFigmaCredits(cost);

  if (isLoading) {
    return (
      <FlexCenVer sx={{ gap: 0.25, flexShrink: 0, color: token.text, height: infoRowHeight }}>
        <PriceCreditIcon legacy="figma" />
        <Skeleton variant="rectangular" sx={{ borderRadius: '2px' }} width={24} height={12} />
      </FlexCenVer>
    );
  }

  if (chargesFigmaCredits) {
    if (isDynamicCost) {
      return (
        <FlexCenVer sx={{ gap: 1, flexShrink: 0, color: token.text, height: infoRowHeight }}>
          <PriceCreditIcon legacy="figma" />
          <AppInfoIcon tooltipProps={{ title: t(I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.BETA_TOOLTIP_DYNAMIC) }} />
        </FlexCenVer>
      );
    }
    return figmaCredits === undefined ? null : (
      <FlexCenVer sx={{ gap: 0.25, flexShrink: 0, color: token.text, height: infoRowHeight }}>
        <PriceCreditIcon legacy="figma" />
        <TextSmMd>{figmaCredits}</TextSmMd>
      </FlexCenVer>
    );
  }

  if (isDynamicCost) {
    return (
      <FlexCenVer sx={{ gap: 1, flexShrink: 0, color: token.text, height: infoRowHeight }}>
        <PriceCreditIcon legacy="figma" />
        <AppInfoIcon tooltipProps={{ title: t(I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.BETA_TOOLTIP_DYNAMIC) }} />
        {showBetaLabel && <TextSmRg>{t(I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.FREE_IN_BETA)}</TextSmRg>}
      </FlexCenVer>
    );
  }

  if (figmaCredits === undefined) {
    return showBetaLabel ? (
      <FlexCenVer sx={{ gap: 1, flexShrink: 0, color: token.text, height: infoRowHeight }}>
        <TextSmRg>{t(I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.FREE_IN_BETA)}</TextSmRg>
      </FlexCenVer>
    ) : null;
  }

  // The credit number is the greyed, struck-through post-beta price. When showBetaLabel is set (the
  // properties-panel "Total cost"), the "Free in beta" label is shown beside it; elsewhere the beta
  // context is preserved in the tooltip.
  return (
    <Tooltip
      title={t(I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.BETA_TOOLTIP_STATIC, { count: figmaCredits })}
      placement="top"
      classes={{ tooltip: 'notranslate' }}
    >
      <FlexCenVer sx={{ gap: 1, flexShrink: 0, color: token.text, height: infoRowHeight }}>
        <FlexCenVer sx={{ gap: 0.25, color: color.Black64 }}>
          <PriceCreditIcon legacy="figma" />
          <TextSmMd
            sx={{
              color: color.Black64,
              textDecorationLine: 'line-through',
              textDecorationColor: color.Black64,
            }}
          >
            {figmaCredits}
          </TextSmMd>
        </FlexCenVer>
        {showBetaLabel && <TextSmRg>{t(I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.FREE_IN_BETA)}</TextSmRg>}
      </FlexCenVer>
    </Tooltip>
  );
};

const WeavyPriceInfoCost = ({
  cost,
  isLoading,
  isDynamicCost,
  isPerSecond,
  perSecondRate,
  fullCost,
  promo,
}: PriceInfoCostProps) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const infoRowHeight = isFplMigrationEnabled ? '24px' : '16px';
  const ref = useRef<HTMLElement>(null);
  const dimensions = useContentRect(ref);
  const widthRef = useRef(dimensions?.width || DEFAULT_WIDTH);
  useEffect(() => {
    widthRef.current = dimensions?.width || DEFAULT_WIDTH;
  }, [dimensions]);

  const hasPerSecond = (perSecondRate ?? 0) > 0;
  const hasPerInference = (cost ?? 0) > 0;
  const showSkeleton = isLoading || (cost === undefined && !hasPerSecond);

  if (isDynamicCost) {
    return (
      <FlexCenVer sx={{ gap: 0.25, flexShrink: 0, color: token.text, height: infoRowHeight }}>
        <TextSmRg sx={{ mr: 0.25 }}>{t(I18N_KEYS.RECIPE_MAIN.FLOW.UNKNOWN_COST.PRICE_TEXT)}</TextSmRg>
        <AppInfoIcon tooltipProps={{ title: t(I18N_KEYS.RECIPE_MAIN.FLOW.UNKNOWN_COST.TOOLTIP) }} />
      </FlexCenVer>
    );
  }

  if (showSkeleton) {
    return (
      <FlexCenVer sx={{ gap: 0.25, flexShrink: 0, color: token.text, height: infoRowHeight }}>
        <PriceCreditIcon legacy="weavy" />
        <Typography ref={ref} variant="body-sm-rg">
          <Skeleton variant="rectangular" sx={{ borderRadius: '2px' }} width={widthRef.current} height={12} />
        </Typography>
      </FlexCenVer>
    );
  }

  if (hasPerSecond && hasPerInference) {
    return (
      <FlexCenVer sx={{ gap: 0.25, flexShrink: 0, color: token.text, height: infoRowHeight }}>
        <PriceCreditIcon legacy="weavy" />
        <Typography variant="body-sm-rg">
          {t(I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.CREDITS, { count: Number(roundToDecimalIfNotWhole(cost ?? 0)) })}
        </Typography>
        <Typography variant="body-sm-rg" sx={{ mx: 0.25 }}>
          +
        </Typography>
        <Typography variant="body-sm-rg">
          {t(I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.CREDITS_PER_SECOND, {
            count: Number(roundToDecimalIfNotWhole(perSecondRate ?? 0)),
          })}
        </Typography>
      </FlexCenVer>
    );
  }

  const value = hasPerSecond ? perSecondRate : cost;
  const key =
    hasPerSecond || isPerSecond
      ? I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.CREDITS_PER_SECOND
      : I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.CREDITS;

  // A promotion only discounts the per-inference (flat) cost, so surface the strikethrough on the flat path.
  const hasPromo = !hasPerSecond && fullCost !== undefined && cost !== undefined && fullCost > cost;

  const content = (
    <FlexCenVer sx={{ gap: 0.5, flexShrink: 0, color: token.text, height: infoRowHeight }}>
      <FlexCenVer sx={{ gap: 0.25 }}>
        <PriceCreditIcon legacy="weavy" />
        <Typography ref={ref} variant="body-sm-rg">
          {t(key, { count: Number(roundToDecimalIfNotWhole(value ?? 0)) })}
        </Typography>
      </FlexCenVer>
      {hasPromo && (
        <Typography
          variant="body-sm-rg"
          sx={{
            color: token.textSecondary,
            textDecorationLine: 'line-through',
            textDecorationColor: token.textSecondary,
          }}
        >
          {t(key, { count: Number(roundToDecimalIfNotWhole(fullCost)) })}
        </Typography>
      )}
    </FlexCenVer>
  );

  if (!hasPromo || cost === undefined || fullCost === undefined) {
    return content;
  }
  return (
    <Tooltip title={getPromoTooltipTitle(t, cost, fullCost, promo?.expiresAt)} placement="top">
      {content}
    </Tooltip>
  );
};

export const PriceInfo = ({
  cost,
  perSecondRate,
  isLoading,
  longTitle = false,
  isDynamicCost = false,
  isByok = false,
  isPerSecond = false,
  showBetaLabel = false,
  fullCost,
  promo,
}: {
  cost?: number;
  perSecondRate?: number;
  isLoading: boolean;
  longTitle?: boolean;
  isDynamicCost?: boolean;
  isByok?: boolean;
  isPerSecond?: boolean;
  showBetaLabel?: boolean;
  fullCost?: number;
  promo?: { expiresAt?: string };
}) => {
  const { t } = useTranslation();
  const isFigmaEmbedMode = useIsFigmaEmbedMode();
  const isFplMigrationEnabled = useFplMigrationFlag();

  if (typeof cost === 'number' && cost < 0) {
    return null;
  }

  const hasPerSecond = (perSecondRate ?? 0) > 0;

  // Avoid flashing the standalone pill while `cost` is still resolving — wait for `isLoading` to settle so we don't
  // briefly show "BYOK pill only" before transitioning to "credits + small tag" once the corkage cost arrives.
  // Skip when there's a per-second rate to display: a per-second BYOK model has a meaningful rate to show.
  // In Figma embed mode, billing is owned by Figma's surrounding chrome ("Free in beta" is the dominant signal),
  // so the BYOK indicator is suppressed to avoid mixed messaging.
  if (isByok && !cost && !hasPerSecond && !isLoading && !isFigmaEmbedMode) {
    return (
      <FlexCenVer sx={{ justifyContent: 'flex-end', width: '100%' }}>
        <ByokBadge size="md" />
      </FlexCenVer>
    );
  }

  return (
    <FlexCenVer sx={{ gap: 1, justifyContent: 'space-between', width: '100%' }}>
      {isFplMigrationEnabled ? (
        <Text.span color="secondary" variant="body-medium">
          {t(I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.TITLE, { context: longTitle ? 'long' : 'short' })}
        </Text.span>
      ) : (
        <Typography variant="label-xs-rg" sx={{ color: token.textSecondary }}>
          {t(I18N_KEYS.COMMON_COMPONENTS.PRICE_INFO.TITLE, { context: longTitle ? 'long' : 'short' })}
        </Typography>
      )}
      {isFigmaEmbedMode ? (
        <FigmaPriceInfoCost
          cost={cost}
          isLoading={isLoading}
          isDynamicCost={isDynamicCost}
          showBetaLabel={showBetaLabel}
        />
      ) : (
        <WeavyPriceInfoCost
          cost={cost}
          perSecondRate={perSecondRate}
          isLoading={isLoading}
          isDynamicCost={isDynamicCost}
          isPerSecond={isPerSecond}
          fullCost={fullCost}
          promo={promo}
        />
      )}
      {isByok && !isFigmaEmbedMode && <ByokBadge />}
    </FlexCenVer>
  );
};
